import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import type { ActivityItem, ActivityParams } from '@/types/activity'
import type { CommonPagination } from '@/types/common'
import TitleHeader from '@/components/TitleHeader'
import { Card, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import FilterSection from '@/components/Logs/FilterSection'
import ActivityTable from '@/components/Logs/ActivityTable'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { LogsService } from '@/services/logs'

export const Route = createFileRoute('/_authenticated/activity/logs/')({
  component: LogsManagement,
})

function LogsManagement() {
  const [query, setQuery] = useState<ActivityParams>({ take: 10, page: 1 })
  const [dataLogs, setDataLogs] = useState<CommonPagination<ActivityItem>>({
    items: [],
    meta: {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
      hasNext: false,
      hasPrev: false,
    },
  })

  const mutationGetList = useMutation({
    mutationFn: (params: ActivityParams) => {
      return LogsService.getList(params)
    },
    onSuccess: (response) => {
      setDataLogs(response.data)
    },
  })

  useEffect(() => {
    mutationGetList.mutate({ take: 10, page: 1 })
  }, [])

  const handleSearch = (p?: ActivityParams) => {
    mutationGetList.mutate(p || query)
  }

  const handleChange = (
    field: string,
    value: string | Date | undefined,
    triggerSearch = false,
  ) => {
    const newQuery = { ...query, [field]: value }
    setQuery(newQuery)
    if (triggerSearch) {
      handleSearch(newQuery)
    }
  }

  const handleClear = () => {
    setQuery({
      take: 10,
      page: 1,
      code: undefined,
      userId: '',
      username: '',
      deviceName: '',
      ipAddress: '',
    })
    mutationGetList.mutate({ take: 10, page: 1 })
  }

  const hasNextPage = dataLogs.meta.hasNext
  const hasPrevPage = dataLogs.meta.hasPrev

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setQuery((prev) => ({ ...prev, page: (prev.page ?? 1) - 1 }))
      handleSearch({ ...query, page: (query.page ?? 1) - 1 })
    }
  }

  const handleNextPage = () => {
    if (hasNextPage) {
      setQuery((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }))
      handleSearch({ ...query, page: (query.page ?? 1) + 1 })
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative">
      {mutationGetList.isPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70">
          <Loader2 className="animate-spin w-12 h-12 text-primary" />
        </div>
      )}
      <TitleHeader
        title="Lịch sử hoạt động"
        subtitle="Chào mừng bạn đến với Trung tâm thông tin dữ liệu của bạn!"
      />
      <Card className="mt-6 shadow-none">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            Nhật ký hoạt động
          </CardTitle>
        </CardHeader>

        <Separator />

        <div className="">
          <div className="p-6 space-y-6">
            <FilterSection
              params={query}
              handleChange={handleChange}
              handleClear={handleClear}
              handleSearch={handleSearch}
              isLoading={mutationGetList.isPending}
            />
            <ActivityTable items={dataLogs.items} />
          </div>
        </div>

        <Separator />

        <CardFooter className="flex justify-end items-center">
          <div>
            <Pagination>
              <PaginationContent>
                <PaginationItem onClick={handlePrevPage}>
                  <PaginationPrevious />
                </PaginationItem>
                <PaginationItem onClick={handleNextPage}>
                  <PaginationNext />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}
